'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { io } from 'socket.io-client';
import { CONFIG } from 'src/config-global';
import { useSession, useSessions } from 'src/hooks/use-sessions';
import { matchApiService } from 'src/lib/match-api';
import { openPlayApi, newRequestId } from 'src/lib/open-play-api';
import axios, { endpoints } from 'src/utils/axios';
import { DUPR_CATEGORIES } from 'src/constants/skills';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
/**
 * One definition of "this player is on the board", matching the backend's
 * ACTIVE_PARTICIPATION_STATUSES. CANCELLED / REJECTED / LEAVED / DONE /
 * REQUESTED / WAITLISTED rows stay in the list (they carry payment and refund
 * history) but never count as present.
 */
const isActivePlayer = (p) => p?.status === 'JOINED' || p?.status === 'APPROVED';

/**
 * Registered != Ready. Older backends send no `availability`; treat those rows
 * as READY so a new admin build keeps working against them.
 */
const availabilityOf = (p) => p?.availability ?? (p?.is_on_break ? 'ON_BREAK' : 'READY');
const isReady = (p) => availabilityOf(p) === 'READY' && !p?.is_on_break;
const isOnBreak = (p) => availabilityOf(p) === 'ON_BREAK' || !!p?.is_on_break;
const isNotReady = (p) => availabilityOf(p) === 'NOT_READY' && !p?.is_on_break;

/**
 * The board reads matches as MatchTeam → MatchTeamPlayer. The session-matches
 * list and the Open Play mutation responses both send `teams[].players`;
 * normalise either into the board's shape.
 */
const toBoardMatch = (m) => {
  if (m.MatchTeam) return m;
  return {
    ...m,
    MatchTeam: (m.teams || []).map((t) => ({
      ...t,
      MatchTeamPlayer: (t.players || []).map((p) => ({
        player_id: p.id || p.user_id,
        user_id: p.id || p.user_id,
        player: p,
        user: p,
      })),
    })),
  };
};

/**
 * The board must never hang on one request: the admin axios instance has no
 * timeout, and a single stuck fetch used to stall every refresh after it.
 */
const BOARD_FETCH_TIMEOUT_MS = 15000;
const withTimeout = (promise, ms = BOARD_FETCH_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Request timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });

const waitClock = (p) => new Date(p.ready_at || p.joined_at);

/**
 * The order FIFO rotation picks in, so the next player on the Waiting list is
 * the next one picked. The server numbers it (`wait_position`). The rest is the
 * same rule — Ready first, then registered first — for a backend that predates
 * the field; players who come off one court together share a wait clock, so
 * the clock alone does not order them.
 */
const byQueueOrder = (a, b) => {
  if (a.wait_position != null && b.wait_position != null) {
    return a.wait_position - b.wait_position;
  }
  return (
    waitClock(a) - waitClock(b) ||
    new Date(a.joined_at) - new Date(b.joined_at) ||
    String(a.user_id).localeCompare(String(b.user_id))
  );
};

/** A queued lineup's players as the board's player rows (falling back to the match's own data). */
const lineupPlayers = (match, teamNumber, playerStats) => {
  const team = (match?.MatchTeam || []).find((t) => t.team_number === teamNumber);
  return (team?.MatchTeamPlayer || []).map((p) => {
    const id = p.player_id || p.user_id;
    return (
      playerStats.find((row) => row.user_id === id) || { user_id: id, user: p.player || p.user }
    );
  });
};

/**
 * When a rotation sends a queued pair to challenge winners, that pair has left
 * its lineup. Mirror it locally so the queue card does not show them twice
 * until the refresh lands.
 */
const applyQueueUses = (matches, outcomes) => {
  const challenges = (outcomes || [])
    .map((o) => o?.from_queue)
    .filter((q) => q?.kind === 'CHALLENGE');
  if (!challenges.length) return matches;
  return matches
    .map((m) => {
      const used = challenges.filter((q) => q.match_id === m.id);
      if (!used.length || m.status !== 'PENDING' || m.court_id) return m;
      const sides = used.map((q) => (q.side === 'AWAY' ? 2 : 1));
      return {
        ...m,
        MatchTeam: (m.MatchTeam || []).map((t) =>
          sides.includes(t.team_number) ? { ...t, MatchTeamPlayer: [] } : t
        ),
      };
    })
    .filter(
      (m) =>
        !challenges.some((q) => q.match_id === m.id) ||
        (m.MatchTeam || []).some((t) => (t.MatchTeamPlayer || []).length > 0)
    );
};

/**
 * Everything a completion response tells us about the board: the finished
 * match, the court the winners now hold (when nobody filled it yet), and any
 * matches auto-rotation just started.
 */
const matchesFromCompletion = (result) => {
  if (!result?.match) return [];
  const rotated = (result.auto_rotation?.matches || []).map((o) => o?.match).filter(Boolean);
  const out = [result.match];
  const held = result.held_match;
  if (held && !rotated.some((m) => m.id === held.id)) {
    const holders = (result.match.teams || [])
      .flatMap((t) => t.players || [])
      .filter((p) => (held.holder_ids || []).includes(p.id));
    out.push({
      id: held.id,
      session_id: result.match.session_id,
      court_id: held.court_id,
      court: result.match.court,
      status: 'PENDING',
      created_via: 'WINNERS_STAY',
      teams: [
        {
          id: `${held.id}-holders`,
          team_number: held.side === 'AWAY' ? 2 : 1,
          side: held.side,
          score: 0,
          is_winner: false,
          players: holders,
        },
      ],
    });
  }
  return [...out, ...rotated];
};

const OpenPlayContext = createContext(null);

export const useOpenPlay = () => {
  const ctx = useContext(OpenPlayContext);
  if (!ctx) throw new Error('useOpenPlay must be used within OpenPlayProvider');
  return ctx;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export function OpenPlayProvider({ children }) {
  // ---- session list (only ONGOING) ----
  const {
    sessions,
    loading: sessionsLoading,
    refreshSessions,
    addPlayerToSession,
    updateSession,
  } = useSessions({ status: 'ONGOING' });
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  // auto-select first session
  useEffect(() => {
    if (!selectedSessionId && sessions?.length) {
      setSelectedSessionId(sessions[0]?.id);
    }
  }, [sessions, selectedSessionId]);

  useEffect(() => {
    if (sessionsLoading || !selectedSessionId) return;

    const exists = (sessions || []).some((s) => s.id === selectedSessionId);
    if (!exists) {
      setSelectedSessionId(sessions?.[0]?.id || null);
    }
  }, [sessions, sessionsLoading, selectedSessionId]);

  // ---- selected session detail ----
  const {
    session,
    loading: sessionLoading,
    refetch: refetchSession,
  } = useSession(selectedSessionId);

  // ---- player stats ----
  const [playerStats, setPlayerStats] = useState([]);
  // Server-computed counters. The board used to derive "Total Players" from
  // playerStats.length, which counted cancelled/left/done rows — that is how a
  // CANCELLED walk-in kept the total at 3 when only 2 players were active.
  const [playerCounts, setPlayerCounts] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const statsInitRef = useRef(false);

  // Each board fetcher numbers its requests. Only the NEWEST one for the
  // session still on screen may write state: an older response landing late
  // (a slow refresh overtaken by a newer one, or the previous session's fetch
  // arriving after a switch) is dropped instead of overwriting fresher data.
  const fetchSeqRef = useRef({ stats: 0, matches: 0, rotation: 0 });
  const sessionIdRef = useRef(selectedSessionId);
  sessionIdRef.current = selectedSessionId;
  const beginFetch = useCallback((kind, sessionId) => {
    fetchSeqRef.current[kind] += 1;
    const seq = fetchSeqRef.current[kind];
    return () => seq === fetchSeqRef.current[kind] && sessionIdRef.current === sessionId;
  }, []);

  // True when the last refresh could not load part of the board: what is on
  // screen may be out of date, and the header says so instead of pretending.
  const [refreshError, setRefreshError] = useState(false);

  /** Each fetcher resolves `true` when the board is up to date for it, `false` when it failed. */
  const fetchPlayerStats = useCallback(async () => {
    if (!selectedSessionId) return true;
    const isCurrent = beginFetch('stats', selectedSessionId);
    try {
      if (!statsInitRef.current) setStatsLoading(true);
      const res = await withTimeout(axios.get(endpoints.openPlay.playerStats(selectedSessionId)));
      if (!isCurrent()) return true;
      if (res.data?.status !== 'success') {
        throw new Error(res.data?.message || 'Failed to load players');
      }
      setPlayerStats(res.data.data.players || []);
      setPlayerCounts(res.data.data.counts ?? null);
      return true;
    } catch (err) {
      console.error('Failed to fetch player stats:', err);
      return !isCurrent();
    } finally {
      if (isCurrent()) {
        setStatsLoading(false);
        statsInitRef.current = true;
      }
    }
  }, [selectedSessionId, beginFetch]);

  useEffect(() => {
    statsInitRef.current = false;
    // Never show the previous session's players under the new session's name.
    setPlayerStats([]);
    setPlayerCounts(null);
    fetchPlayerStats();
  }, [fetchPlayerStats]);

  // ---- matches for session ----
  const [sessionMatches, setSessionMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const matchesInitRef = useRef(false);
  const [matchCompletionTimes, setMatchCompletionTimes] = useState({});

  const fetchSessionMatches = useCallback(async () => {
    if (!selectedSessionId) return true;
    const isCurrent = beginFetch('matches', selectedSessionId);
    try {
      // Only show loading spinner on initial load — not on WS-triggered refetches
      if (!matchesInitRef.current) setMatchesLoading(true);
      const res = await withTimeout(matchApiService.getSessionMatches(selectedSessionId));
      if (!isCurrent()) return true;
      // API returns { total_rounds, total_matches, rounds: [{ round_number, matches }] }
      // Flatten all matches from all rounds into a single array
      let matches;
      if (Array.isArray(res?.rounds)) {
        matches = res.rounds.flatMap((r) => r.matches || []);
      } else if (Array.isArray(res?.data?.rounds)) {
        matches = res.data.rounds.flatMap((r) => r.matches || []);
      } else if (Array.isArray(res)) {
        matches = res;
      } else {
        // An answer we cannot read is a failure, not "this session has no
        // matches": emptying the board on it would hide every court.
        throw new Error(res?.message || 'Unexpected matches response');
      }
      setSessionMatches(matches.map(toBoardMatch));
      return true;
    } catch (err) {
      console.error('Failed to fetch session matches:', err);
      return !isCurrent();
    } finally {
      if (isCurrent()) {
        setMatchesLoading(false);
        matchesInitRef.current = true;
      }
    }
  }, [selectedSessionId, beginFetch]);

  useEffect(() => {
    matchesInitRef.current = false;
    setSessionMatches([]);
    fetchSessionMatches();
  }, [fetchSessionMatches]);

  /**
   * Put the matches a mutation just returned on the board straight away. The
   * refresh that follows every mutation replaces them with the server's list.
   */
  const applyMatchUpdates = useCallback((matches) => {
    const updates = (matches || []).filter((m) => m?.id).map(toBoardMatch);
    if (!updates.length) return;
    setSessionMatches((prev) => {
      const byId = new Map(updates.map((m) => [m.id, m]));
      const known = new Set(prev.map((m) => m.id));
      return [
        ...prev.map((m) => (byId.has(m.id) ? { ...m, ...byId.get(m.id) } : m)),
        ...updates.filter((m) => !known.has(m.id)),
      ];
    });
  }, []);

  /** Take matches off the board at once (a removal the server just confirmed). */
  const dropMatches = useCallback((ids) => {
    const gone = new Set((ids || []).filter(Boolean));
    if (!gone.size) return;
    setSessionMatches((prev) => prev.filter((m) => !gone.has(m.id)));
  }, []);

  // ---- WebSocket (refs declared here, effect set up after all fetch functions) ----
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  // ---- derived state ----
  const courts = useMemo(() => {
    // Try facility_reservations first
    if (session?.facility_reservations?.length) {
      const courtIds = session.facility_reservations.flatMap((r) => r.court_ids || []);
      const facilityRes = session.facility_reservations[0];
      const facilityCourts = facilityRes?.facility?.courts || [];
      if (facilityCourts.length) {
        // If court_ids is empty, all facility courts are assigned
        const filtered = courtIds.length
          ? facilityCourts.filter((c) => courtIds.includes(c.id) && c.status === 'ACTIVE')
          : facilityCourts.filter((c) => c.status === 'ACTIVE');
        if (filtered.length) {
          return filtered.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        }
      }
    }
    // Fallback: courts from the session's direct facility relation
    if (session?.facility?.courts?.length) {
      return session.facility.courts
        .filter((c) => c.status === 'ACTIVE')
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }
    return [];
  }, [session]);

  const activeMatches = useMemo(
    () => sessionMatches.filter((m) => m.status === 'IN_PROGRESS'),
    [sessionMatches]
  );

  const pendingMatches = useMemo(
    () => sessionMatches.filter((m) => m.status === 'PENDING'),
    [sessionMatches]
  );

  // ---- session rules (extracted from session) ----
  const sessionRules = useMemo(
    () => ({
      queue_mode: session?.queue_mode || 'FIFO',
      max_consecutive_wins: session?.max_consecutive_wins ?? null,
      losers_go_to: session?.losers_go_to || 'BACK_TO_QUEUE',
      winners_stay_on_court: session?.winners_stay_on_court ?? true,
      winners_stay_on_side: session?.winners_stay_on_side || 'HOME_SIDE',
      allow_cross_skill_mixing: session?.allow_cross_skill_mixing ?? false,
      queue_time_warning_minutes: session?.queue_time_warning_minutes ?? 5,
      rotation_mode: session?.rotation_mode || 'MANUAL',
    }),
    [session]
  );

  // ---- rotation state (backend-owned: eligible pool, court availability, blockers) ----
  const [rotationState, setRotationState] = useState(null);
  const [rotationLoading, setRotationLoading] = useState(false);
  const rotationInitRef = useRef(false);

  const fetchRotationState = useCallback(async () => {
    if (!selectedSessionId) return true;
    const isCurrent = beginFetch('rotation', selectedSessionId);
    try {
      if (!rotationInitRef.current) setRotationLoading(true);
      const data = await withTimeout(openPlayApi.getRotationState(selectedSessionId));
      if (!isCurrent()) return true;
      setRotationState(data);
      return true;
    } catch (err) {
      console.error('Failed to fetch rotation state:', err);
      return !isCurrent();
    } finally {
      if (isCurrent()) {
        setRotationLoading(false);
        rotationInitRef.current = true;
      }
    }
  }, [selectedSessionId, beginFetch]);

  useEffect(() => {
    rotationInitRef.current = false;
    setRotationState(null);
    setRefreshError(false);
    fetchRotationState();
  }, [fetchRotationState]);

  // What the SERVER says about each court: free, held by winners, or busy
  // (including "another session has a match on it"). The board draws its
  // court cards from the matches it holds; this decides what may be done.
  const courtAvailability = useMemo(() => {
    if (!rotationState) return null;
    return {
      free: new Set((rotationState.free_courts || []).map((c) => c.id)),
      held: new Map((rotationState.held_courts || []).map((h) => [h.court.id, h])),
      busy: new Map((rotationState.busy_courts || []).map((b) => [b.court.id, b])),
    };
  }, [rotationState]);

  // ---- refresh: one coalesced wave at a time ----
  // Socket bursts are debounced, two waves never run side by side (a request
  // that arrives mid-wave runs exactly once more afterwards), and while this
  // admin's own mutation is in flight socket events only mark what is stale —
  // the refresh after the mutation covers everything that happened before it,
  // so the board never fetches a half-finished state mid-request.
  const refreshTimerRef = useRef(null);
  const pendingRefreshRef = useRef({ matches: false, players: false });
  const refreshRunningRef = useRef(null);
  const refreshAgainRef = useRef(false);
  const mutationsInFlightRef = useRef(0);

  const runRefresh = useCallback(() => {
    if (refreshRunningRef.current) {
      refreshAgainRef.current = true;
      return refreshRunningRef.current;
    }
    const wave = async () => {
      refreshAgainRef.current = false;
      const pending = pendingRefreshRef.current;
      pendingRefreshRef.current = { matches: false, players: false };
      const fetches = [];
      if (pending.matches) fetches.push(fetchSessionMatches());
      if (pending.players) fetches.push(fetchPlayerStats());
      // The Ready pool / court availability moves with players and matches.
      if (pending.matches || pending.players) fetches.push(fetchRotationState());
      const results = await Promise.all(fetches).catch(() => [false]);
      const ok = !results.includes(false);
      if (refreshAgainRef.current) return wave();
      setRefreshError(!ok);
      return ok;
    };
    const running = wave().finally(() => {
      refreshRunningRef.current = null;
    });
    refreshRunningRef.current = running;
    return running;
  }, [fetchSessionMatches, fetchPlayerStats, fetchRotationState]);

  const scheduleRefresh = useCallback(
    ({ matches, players } = {}) => {
      if (matches) pendingRefreshRef.current.matches = true;
      if (players) pendingRefreshRef.current.players = true;
      // Our own request is still running; its refresh will pick these up.
      if (mutationsInFlightRef.current > 0) return;
      if (refreshTimerRef.current) return; // already scheduled
      refreshTimerRef.current = setTimeout(() => {
        refreshTimerRef.current = null;
        runRefresh();
      }, 300);
    },
    [runRefresh]
  );

  /**
   * Refetch everything a mutation can change, in ONE settle. Cancelling the
   * pending debounce first matters: a mutation used to fire its own refetch AND
   * the socket's coalesced one, and whichever landed second won — which is how
   * the counters could flash 3 → 2 → 3.
   */
  const refreshAfterMutation = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    pendingRefreshRef.current = { matches: true, players: true };
    return runRefresh();
  }, [runRefresh]);

  /**
   * Run one board mutation. Resolves as soon as the server answers — the
   * caller closes its dialog and toasts right away, `matchesFrom(response)`
   * puts the new matches on the board, and the confirming refresh runs in the
   * background instead of holding the UI.
   */
  const runMutation = useCallback(
    async (request, matchesFrom) => {
      mutationsInFlightRef.current += 1;
      try {
        const res = await request();
        if (matchesFrom) applyMatchUpdates(matchesFrom(res));
        return res;
      } finally {
        mutationsInFlightRef.current -= 1;
        refreshAfterMutation();
      }
    },
    [applyMatchUpdates, refreshAfterMutation]
  );

  /**
   * One request per action at a time. A second click that lands before React
   * has re-rendered the disabled button joins the request already running
   * instead of sending another; the server-side request id covers the rest.
   */
  const inFlightRef = useRef(new Map());
  const exclusive = useCallback((key, fn) => {
    const running = inFlightRef.current.get(key);
    if (running) return running;
    const request = Promise.resolve()
      .then(fn)
      .finally(() => inFlightRef.current.delete(key));
    inFlightRef.current.set(key, request);
    return request;
  }, []);

  const completedMatches = useMemo(
    () => sessionMatches.filter((m) => m.status === 'COMPLETED'),
    [sessionMatches]
  );

  // Queue matches = PENDING with no court
  const queueMatches = useMemo(() => pendingMatches.filter((m) => !m.court_id), [pendingMatches]);

  // The server classifies every player (`bucket`) with the same rule its
  // counters use, so the lists below can never disagree with the stat cards.
  // The match-based derivation is the fallback for a backend that predates it.
  const hasBuckets = useMemo(() => playerStats.some((p) => p.bucket !== undefined), [playerStats]);
  const inBucket = useCallback(
    (bucket) => playerStats.filter((p) => p.bucket === bucket),
    [playerStats]
  );

  // waiting players = the rotation pool: active + READY + not in an active/pending match
  const waitingPlayers = useMemo(() => {
    if (hasBuckets) return inBucket('WAITING').sort(byQueueOrder);

    const activePlayerIds = new Set();
    [...activeMatches, ...pendingMatches].forEach((m) => {
      m.MatchTeam?.forEach((team) => {
        team.MatchTeamPlayer?.forEach((p) => activePlayerIds.add(p.player_id));
      });
    });

    return playerStats
      .filter((p) => isActivePlayer(p) && isReady(p) && !activePlayerIds.has(p.user_id))
      .map((p) => {
        const completionTime = matchCompletionTimes[p.user_id];
        if (completionTime && new Date(completionTime) > waitClock(p)) {
          return { ...p, joined_at: completionTime };
        }
        return p;
      })
      .sort(byQueueOrder);
  }, [hasBuckets, inBucket, playerStats, activeMatches, pendingMatches, matchCompletionTimes]);

  // These four lists mirror the backend's bucketParticipant() precedence —
  // on court > in queue > on break > waiting — so a player can never appear in
  // two of them and the lists can never disagree with the server's counts.
  const playersOnCourt = useMemo(() => {
    if (hasBuckets) return inBucket('ON_COURT');
    const ids = new Set();
    activeMatches.forEach((m) => {
      m.MatchTeam?.forEach((team) => {
        team.MatchTeamPlayer?.forEach((p) => ids.add(p.player_id));
      });
    });
    return playerStats.filter((p) => ids.has(p.user_id) && isActivePlayer(p));
  }, [hasBuckets, inBucket, playerStats, activeMatches]);

  // Players in queue (staged lineups, not on a court)
  const playersInQueue = useMemo(() => {
    if (hasBuckets) return inBucket('IN_QUEUE');
    const onCourtIds = new Set(playersOnCourt.map((p) => p.user_id));
    const ids = new Set();
    pendingMatches.forEach((m) => {
      m.MatchTeam?.forEach((team) => {
        team.MatchTeamPlayer?.forEach((p) => ids.add(p.player_id));
      });
    });
    return playerStats.filter(
      (p) => ids.has(p.user_id) && isActivePlayer(p) && !onCourtIds.has(p.user_id)
    );
  }, [hasBuckets, inBucket, playerStats, pendingMatches, playersOnCourt]);

  // Declared after the two above so it can subtract them: a player mid-match
  // who is also flagged on-break belongs on the court, not the break list.
  const playersOnBreak = useMemo(() => {
    if (hasBuckets) return inBucket('ON_BREAK');
    const busyIds = new Set([...playersOnCourt, ...playersInQueue].map((p) => p.user_id));
    return playerStats.filter((p) => isOnBreak(p) && isActivePlayer(p) && !busyIds.has(p.user_id));
  }, [hasBuckets, inBucket, playerStats, playersOnCourt, playersInQueue]);

  // Registered but not yet at the facility — never in the rotation pool.
  const playersNotReady = useMemo(() => {
    if (hasBuckets) return inBucket('NOT_READY');
    const busyIds = new Set([...playersOnCourt, ...playersInQueue].map((p) => p.user_id));
    return playerStats.filter(
      (p) => isNotReady(p) && isActivePlayer(p) && !busyIds.has(p.user_id)
    );
  }, [hasBuckets, inBucket, playerStats, playersOnCourt, playersInQueue]);

  // Group waiting players by skill category
  const waitingBySkill = useMemo(() => {
    const groups = {};
    DUPR_CATEGORIES.forEach((cat) => {
      groups[cat.key] = [];
    });
    groups.NOT_SET = [];

    waitingPlayers.forEach((p) => {
      const level = p.user?.skill_level;
      if (level == null) {
        groups.NOT_SET.push(p);
        return;
      }
      const cat = DUPR_CATEGORIES.find((c) => level >= c.min && level <= c.max);
      if (cat) groups[cat.key].push(p);
      else groups.NOT_SET.push(p);
    });
    return groups;
  }, [waitingPlayers]);

  // ---- actions ----
  const togglePlayerBreak = useCallback(
    async (userId) => {
      if (!selectedSessionId) return;
      const res = await runMutation(() =>
        axios.post(endpoints.openPlay.toggleBreak(selectedSessionId, userId))
      );
      return res.data;
    },
    [selectedSessionId, runMutation]
  );

  const updatePlayerStatus = useCallback(
    async (userId, status) => {
      if (!selectedSessionId) return;
      const res = await runMutation(() =>
        axios.patch(endpoints.openPlay.updatePlayerStatus(selectedSessionId, userId), {
          status,
        })
      );
      return res.data;
    },
    [selectedSessionId, runMutation]
  );

  const assignPlayerToMatch = useCallback(
    async (matchId, teamNumber, playerId) => {
      // This is handled by modifying the match teams
      // For now, we use create single match approach
      await fetchSessionMatches();
      await fetchPlayerStats();
    },
    [fetchSessionMatches, fetchPlayerStats]
  );

  /** Manual "New Queue": an empty lineup (or a full one), created under the session lock. */
  const createQueueMatch = useCallback(
    async (homePlayerIds = [], awayPlayerIds = []) => {
      if (!selectedSessionId) return undefined;
      return exclusive('new-queue', () => {
        const requestId = newRequestId();
        return runMutation(
          () =>
            openPlayApi.createQueue(selectedSessionId, {
              homePlayerIds,
              awayPlayerIds,
              requestId,
            }),
          (result) => [result?.match]
        );
      });
    },
    [selectedSessionId, runMutation, exclusive]
  );

  /** Put a queued lineup on a court. The server checks the court and all four players. */
  const assignQueueToCourt = useCallback(
    async (matchId, courtId) => {
      if (!selectedSessionId) return undefined;
      return exclusive(`assign:${matchId}`, () =>
        runMutation(
          () => openPlayApi.assignQueueToCourt(selectedSessionId, matchId, courtId),
          (outcome) => [outcome?.match]
        )
      );
    },
    [selectedSessionId, runMutation, exclusive]
  );

  /**
   * Take a match off the board without a result: a queued lineup, winners
   * holding a court, or a match in play. Its players return to Ready.
   */
  const removeMatch = useCallback(
    async (matchId) => {
      if (!selectedSessionId) return undefined;
      return exclusive(`remove:${matchId}`, () =>
        runMutation(async () => {
          const removed = await openPlayApi.removeMatch(selectedSessionId, matchId);
          dropMatches([removed?.match_id || matchId]);
          return removed;
        })
      );
    },
    [selectedSessionId, runMutation, exclusive, dropMatches]
  );

  /** Free a court that a finished session left a live match on. */
  const clearCourt = useCallback(
    async (courtId) => {
      if (!selectedSessionId) return undefined;
      return exclusive(`clear:${courtId}`, () =>
        runMutation(() => openPlayApi.clearCourt(selectedSessionId, courtId))
      );
    },
    [selectedSessionId, runMutation, exclusive]
  );

  const setMatchWinner = useCallback(
    async (matchId, winnerTeamId) => {
      // Capture player IDs from the match before completing it
      const match = sessionMatches.find((m) => m.id === matchId);
      const playerIds = [];
      match?.MatchTeam?.forEach((team) => {
        team.MatchTeamPlayer?.forEach((p) => {
          playerIds.push(p.player_id || p.user_id);
        });
      });

      const res = await matchApiService.setMatchWinner(matchId, winnerTeamId);

      // Record completion time for all players in this match
      if (playerIds.length > 0) {
        const now = new Date().toISOString();
        setMatchCompletionTimes((prev) => {
          const updated = { ...prev };
          playerIds.forEach((pid) => {
            updated[pid] = now;
          });
          return updated;
        });
      }

      await fetchSessionMatches();
      await fetchPlayerStats();
      return res;
    },
    [sessionMatches, fetchSessionMatches, fetchPlayerStats]
  );

  // ---- rotation & queue actions ----
  const completeAndRotate = useCallback(
    async (matchId, winnerTeamId) => {
      if (!selectedSessionId) return;

      // Capture player IDs from the match before completing it
      const match = sessionMatches.find((m) => m.id === matchId);
      const playerIds = [];
      match?.MatchTeam?.forEach((team) => {
        team.MatchTeamPlayer?.forEach((p) => {
          playerIds.push(p.player_id || p.user_id);
        });
      });

      const res = await axios.post(
        endpoints.openPlay.completeAndRotate(selectedSessionId, matchId),
        { winner_team_id: winnerTeamId }
      );

      // Record completion time for all players in this match
      if (playerIds.length > 0) {
        const now = new Date().toISOString();
        setMatchCompletionTimes((prev) => {
          const updated = { ...prev };
          playerIds.forEach((pid) => {
            updated[pid] = now;
          });
          return updated;
        });
      }

      // Don't manually refetch here — WebSocket events (rotation_completed,
      // session_changed) will trigger a single debounced refresh.
      return res.data;
    },
    [selectedSessionId, sessionMatches]
  );

  const [queueStatus, setQueueStatus] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);

  const fetchQueueStatus = useCallback(async () => {
    if (!selectedSessionId) return;
    try {
      setQueueLoading(true);
      const res = await axios.get(endpoints.openPlay.queueStatus(selectedSessionId));
      if (res.data?.status === 'success') {
        setQueueStatus(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch queue status:', err);
    } finally {
      setQueueLoading(false);
    }
  }, [selectedSessionId]);
  // Legacy queue_courts data: no board component reads it, so it is only
  // fetched by the legacy queue actions below, never on load or refresh.

  const promoteFromQueue = useCallback(
    async (courtId, playersNeeded = 4) => {
      if (!selectedSessionId) return;
      const res = await axios.post(endpoints.openPlay.promoteFromQueue(selectedSessionId), {
        court_id: courtId,
        players_needed: playersNeeded,
      });
      await Promise.all([fetchQueueStatus(), fetchSessionMatches(), fetchPlayerStats()]);
      return res.data;
    },
    [selectedSessionId, fetchQueueStatus, fetchSessionMatches, fetchPlayerStats]
  );

  const addToQueue = useCallback(
    async (userId, queueType) => {
      if (!selectedSessionId) return;
      const res = await axios.post(endpoints.openPlay.addToQueue(selectedSessionId), {
        user_id: userId,
        queue_type: queueType,
      });
      await Promise.all([fetchQueueStatus(), fetchPlayerStats()]);
      return res.data;
    },
    [selectedSessionId, fetchQueueStatus, fetchPlayerStats]
  );

  const removeFromQueue = useCallback(
    async (userId) => {
      if (!selectedSessionId) return;
      const res = await axios.post(endpoints.openPlay.removeFromQueue(selectedSessionId), {
        user_id: userId,
      });
      await Promise.all([fetchQueueStatus(), fetchPlayerStats()]);
      return res.data;
    },
    [selectedSessionId, fetchQueueStatus, fetchPlayerStats]
  );

  const reorderQueue = useCallback(
    async (orderedUserIds) => {
      if (!selectedSessionId) return;
      const res = await axios.post(endpoints.openPlay.reorderQueue(selectedSessionId), {
        user_ids: orderedUserIds,
      });
      await fetchQueueStatus();
      return res.data;
    },
    [selectedSessionId, fetchQueueStatus]
  );

  const [openPlayStats, setOpenPlayStats] = useState(null);
  const [openPlayStatsLoading, setOpenPlayStatsLoading] = useState(false);

  const fetchOpenPlayStats = useCallback(async () => {
    if (!selectedSessionId) return;
    try {
      setOpenPlayStatsLoading(true);
      const res = await axios.get(endpoints.openPlay.openPlayStats(selectedSessionId));
      if (res.data?.status === 'success') {
        setOpenPlayStats(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch open play stats:', err);
    } finally {
      setOpenPlayStatsLoading(false);
    }
  }, [selectedSessionId]);
  // Not fetched on load or refresh: no board component reads it.

  // ---- rotation actions (all validated server-side under the session lock) ----
  // Each resolves when the server answers; the board refresh follows in the background.
  const setPlayerAvailability = useCallback(
    async (userId, availability) => {
      if (!selectedSessionId) return undefined;
      return runMutation(() =>
        openPlayApi.setPlayerAvailability(selectedSessionId, userId, availability)
      );
    },
    [selectedSessionId, runMutation]
  );

  const startRotation = useCallback(async () => {
    if (!selectedSessionId) return undefined;
    return exclusive('rotation', () => {
      const requestId = newRequestId();
      return runMutation(async () => {
        const result = await openPlayApi.startRotation(selectedSessionId, { requestId });
        // A queued pair that went on to challenge winners has left its lineup.
        setSessionMatches((prev) => applyQueueUses(prev, result?.matches));
        return result;
      }, (result) => (result?.matches || []).map((o) => o?.match));
    });
  }, [selectedSessionId, runMutation, exclusive]);

  /** Smart AI Rotation "New Queue": the server picks the next four Ready players and stages them. */
  const createSmartQueue = useCallback(async () => {
    if (!selectedSessionId) return undefined;
    return exclusive('new-queue', () => {
      const requestId = newRequestId();
      return runMutation(
        () => openPlayApi.createSmartQueue(selectedSessionId, { requestId }),
        (result) => [result?.match]
      );
    });
  }, [selectedSessionId, runMutation, exclusive]);

  const createOpenPlayMatch = useCallback(
    async ({ courtId, homePlayerIds, awayPlayerIds, fillMatchId }) => {
      if (!selectedSessionId) return undefined;
      return runMutation(
        () =>
          openPlayApi.createMatch(selectedSessionId, {
            courtId,
            homePlayerIds,
            awayPlayerIds,
            fillMatchId,
          }),
        (outcome) => [outcome?.match]
      );
    },
    [selectedSessionId, runMutation]
  );

  const completeOpenPlayMatch = useCallback(
    async (matchId, { homeScore, awayScore, winnerSide, autoRotate }) => {
      if (!selectedSessionId) return undefined;
      return exclusive(`complete:${matchId}`, () =>
        runMutation(async () => {
          const result = await openPlayApi.completeMatch(selectedSessionId, matchId, {
            homeScore,
            awayScore,
            winnerSide,
            autoRotate,
          });
          setSessionMatches((prev) => applyQueueUses(prev, result?.auto_rotation?.matches));
          return result;
        }, matchesFromCompletion)
      );
    },
    [selectedSessionId, runMutation, exclusive]
  );

  const resendMatchNotification = useCallback(
    async (matchId) => {
      if (!selectedSessionId) return undefined;
      return runMutation(() => openPlayApi.resendNotifications(selectedSessionId, matchId));
    },
    [selectedSessionId, runMutation]
  );

  const setRotationMode = useCallback(
    async (mode) => {
      if (!selectedSessionId) return;
      await updateSession(selectedSessionId, { rotation_mode: mode });
      await Promise.all([refetchSession(), fetchRotationState()]);
    },
    [selectedSessionId, updateSession, refetchSession, fetchRotationState]
  );

  useEffect(() => {
    if (!selectedSessionId) return undefined;

    const socketUrl = CONFIG.socketUrl || CONFIG.serverUrl.replace('/api', '');
    const socket = io(`${socketUrl}/sessions/${selectedSessionId}`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    let connectedBefore = false;
    socket.on('connect', () => {
      setSocketConnected(true);
      // Events sent while we were disconnected (a backend restart, a network
      // blip) are gone — resync, or the board keeps pre-disconnect state.
      if (connectedBefore) scheduleRefresh({ matches: true, players: true });
      connectedBefore = true;
    });

    socket.on('disconnect', () => setSocketConnected(false));

    // All WS events feed into the debounced refresh — no more multiple simultaneous fetches.
    // `lineup_updated` is not listened to: it is the mobile payload, and every
    // lineup broadcast is paired with a `session_changed` handled below.
    socket.on('player_break_updated', () => {
      scheduleRefresh({ players: true });
    });

    socket.on('rotation_completed', () => {
      scheduleRefresh({ matches: true, players: true });
    });

    socket.on('queue_updated', () => {
      scheduleRefresh({ players: true });
    });

    socket.on('matches_updated', () => {
      scheduleRefresh({ matches: true, players: true });
    });

    // Match events (now delivered via session namespace)
    socket.on('score_updated', () => {
      scheduleRefresh({ matches: true });
    });

    socket.on('match_completed', () => {
      scheduleRefresh({ matches: true, players: true });
    });

    socket.on('status_changed', () => {
      scheduleRefresh({ matches: true });
    });

    // Generic session change event (player added/removed, queue changes, pushes delivered, etc.)
    socket.on('session_changed', () => {
      scheduleRefresh({ matches: true, players: true });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [selectedSessionId, scheduleRefresh]);

  // Broadcast lineup to mobile clients
  const broadcastLineup = useCallback(
    (lineupData) => {
      if (socketRef.current?.connected && selectedSessionId) {
        socketRef.current.emit('broadcast_lineup', lineupData);
      }
    },
    [selectedSessionId]
  );

  const resetOpenPlayStats = useCallback(async () => {
    if (!selectedSessionId) return;
    const res = await axios.post(endpoints.openPlay.resetStats(selectedSessionId));
    await fetchOpenPlayStats();
    return res.data;
  }, [selectedSessionId, fetchOpenPlayStats]);

  /**
   * The server refuses to complete a session while a match is in play (it
   * needs a result, or to be removed); the caller shows that message.
   */
  const completeSession = useCallback(async () => {
    if (!selectedSessionId) return;

    await updateSession(selectedSessionId, { status: 'COMPLETED' });

    await Promise.all([refreshSessions(), fetchSessionMatches(), fetchPlayerStats()]);
  }, [selectedSessionId, updateSession, refreshSessions, fetchSessionMatches, fetchPlayerStats]);

  const updateMatchScore = useCallback(async (matchId, teamId, score) => {
    const res = await matchApiService.updateMatchScore(matchId, teamId, score);
    return res;
  }, []);

  const deleteQueueMatch = removeMatch;

  // ---- assign mode for waiting players page ----
  // assignMode shape: { courtId?, courtName?, queueMatchId?, queueLabel?, homePlayers: [], awayPlayers: [] }
  const [assignMode, setAssignMode] = useState(null);

  const startAssignMode = useCallback((courtId, courtName) => {
    setAssignMode({ courtId, courtName, homePlayers: [], awayPlayers: [] });
  }, []);

  // Editing a queued lineup starts from who is already in it: the server
  // replaces the whole lineup in one step, so the four sent must be the four wanted.
  const startQueueAssignMode = useCallback(
    (queueMatchId, queueLabel, match) => {
      setAssignMode({
        queueMatchId,
        queueLabel,
        homePlayers: lineupPlayers(match, 1, playerStats),
        awayPlayers: lineupPlayers(match, 2, playerStats),
      });
    },
    [playerStats]
  );

  const cancelAssignMode = useCallback(() => {
    setAssignMode(null);
  }, []);

  const assignPlayerToTeam = useCallback(
    (player, side) => {
      if (!assignMode) return;
      setAssignMode((prev) => {
        const key = side === 'home' ? 'homePlayers' : 'awayPlayers';
        if (prev[key].length >= 2) return prev;
        if (prev[key].some((p) => p.user_id === player.user_id)) return prev;
        return { ...prev, [key]: [...prev[key], player] };
      });
    },
    [assignMode]
  );

  const removePlayerFromTeam = useCallback(
    (userId, side) => {
      if (!assignMode) return;
      setAssignMode((prev) => {
        const key = side === 'home' ? 'homePlayers' : 'awayPlayers';
        return { ...prev, [key]: prev[key].filter((p) => p.user_id !== userId) };
      });
    },
    [assignMode]
  );

  const confirmAssignment = useCallback(async () => {
    if (!assignMode || !selectedSessionId) return;
    const { courtId, queueMatchId, homePlayers, awayPlayers } = assignMode;
    if (homePlayers.length !== 2 || awayPlayers.length !== 2) return;

    const homePlayerIds = homePlayers.map((p) => p.user_id);
    const awayPlayerIds = awayPlayers.map((p) => p.user_id);
    const res = await exclusive('confirm-assignment', () =>
      runMutation(
        () =>
          // Either way ONE server-validated call under the session lock: the
          // players must be Ready and unassigned (a lineup's own members
          // excepted), and a court must be free. The queue path used to delete
          // the lineup and create another in two unguarded requests.
          queueMatchId
            ? openPlayApi.setQueuePlayers(selectedSessionId, queueMatchId, {
                homePlayerIds,
                awayPlayerIds,
              })
            : openPlayApi.createMatch(selectedSessionId, {
                courtId,
                homePlayerIds,
                awayPlayerIds,
              }),
        (outcome) => [outcome?.match]
      )
    );

    setAssignMode(null);
    return res;
  }, [assignMode, selectedSessionId, runMutation, exclusive]);

  // ---- refresh all ----
  // Goes through the same coalesced wave as every other refresh (so it can
  // never race one), and rejects when part of the board could not be loaded —
  // the Refresh button used to say "Refreshed" even when every request failed.
  const refreshAll = useCallback(async () => {
    const [, ok] = await Promise.all([refetchSession(), refreshAfterMutation()]);
    if (ok === false) throw new Error('Part of the board could not be refreshed');
  }, [refetchSession, refreshAfterMutation]);

  const value = useMemo(
    () => ({
      // session
      sessions,
      sessionsLoading,
      selectedSessionId,
      setSelectedSessionId,
      session,
      sessionLoading,
      refreshSessions,
      refetchSession,
      sessionRules,

      // players
      playerStats,
      playerCounts,
      refreshAfterMutation,
      statsLoading,
      fetchPlayerStats,
      waitingPlayers,
      playersOnBreak,
      playersOnCourt,
      playersInQueue,
      playersNotReady,
      waitingBySkill,

      // rotation
      rotationState,
      rotationLoading,
      courtAvailability,
      refreshError,
      removeMatch,
      clearCourt,
      fetchRotationState,
      setPlayerAvailability,
      startRotation,
      createSmartQueue,
      createOpenPlayMatch,
      completeOpenPlayMatch,
      resendMatchNotification,
      setRotationMode,

      // matches
      sessionMatches,
      matchesLoading,
      activeMatches,
      pendingMatches,
      completedMatches,
      queueMatches,
      fetchSessionMatches,

      // courts
      courts,

      // actions
      togglePlayerBreak,
      updatePlayerStatus,
      addPlayerToSession,
      createQueueMatch,
      assignQueueToCourt,
      setMatchWinner,
      updateMatchScore,
      deleteQueueMatch,

      // rotation & queue
      completeAndRotate,
      queueStatus,
      queueLoading,
      fetchQueueStatus,
      promoteFromQueue,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      openPlayStats,
      openPlayStatsLoading,
      fetchOpenPlayStats,
      resetOpenPlayStats,
      completeSession,

      // assign mode
      assignMode,
      startAssignMode,
      startQueueAssignMode,
      cancelAssignMode,
      assignPlayerToTeam,
      removePlayerFromTeam,
      confirmAssignment,

      // socket
      socketConnected,
      broadcastLineup,

      // utility
      refreshAll,
    }),
    [
      sessions,
      sessionsLoading,
      selectedSessionId,
      session,
      sessionLoading,
      sessionRules,
      playerStats,
      playerCounts,
      refreshAfterMutation,
      statsLoading,
      waitingPlayers,
      playersOnBreak,
      playersOnCourt,
      playersInQueue,
      playersNotReady,
      waitingBySkill,
      rotationState,
      rotationLoading,
      courtAvailability,
      refreshError,
      removeMatch,
      clearCourt,
      fetchRotationState,
      setPlayerAvailability,
      startRotation,
      createSmartQueue,
      createOpenPlayMatch,
      completeOpenPlayMatch,
      resendMatchNotification,
      setRotationMode,
      sessionMatches,
      matchesLoading,
      activeMatches,
      pendingMatches,
      completedMatches,
      queueMatches,
      courts,
      togglePlayerBreak,
      updatePlayerStatus,
      addPlayerToSession,
      createQueueMatch,
      assignQueueToCourt,
      setMatchWinner,
      updateMatchScore,
      deleteQueueMatch,
      assignMode,
      startAssignMode,
      startQueueAssignMode,
      cancelAssignMode,
      assignPlayerToTeam,
      removePlayerFromTeam,
      confirmAssignment,
      socketConnected,
      broadcastLineup,
      refreshAll,
      refreshSessions,
      refetchSession,
      fetchPlayerStats,
      fetchSessionMatches,
      setSelectedSessionId,
      completeAndRotate,
      queueStatus,
      queueLoading,
      fetchQueueStatus,
      promoteFromQueue,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      openPlayStats,
      openPlayStatsLoading,
      fetchOpenPlayStats,
      resetOpenPlayStats,
      completeSession,
    ]
  );

  return <OpenPlayContext.Provider value={value}>{children}</OpenPlayContext.Provider>;
}
